import { Fragment, useState, type FormEvent } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, Shield } from 'react-feather';
import { useAuth } from '@/core/auth/useAuth';
import { roleHomePath } from '@/core/permissions';
import { appToast, toastApiError } from '@/core/error/toast';
import { DEMO_PASSWORD, DEV_LOGIN_USERS } from '../api';
import logo from '@/assets/images/logo/logo.png';
import authIllustration from '@/assets/images/login/auth-img.svg';

const FEATURES = [
  'Student academics, attendance, and risk tracking',
  'Career counseling, university & scholarship matching',
  'Admin, Parent, Counselor, University, Employer & Royal portals',
];

/**
 * Credora360 Signin layout — brand panel + form card.
 * @see F:\Projects\credora360\credora360-web\src\Auth\Signin.jsx
 */
export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  const [togglePassword, setTogglePassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const showDemoAccounts = import.meta.env.DEV;

  const canSubmit =
    email.trim().length > 0 && password.length > 0 && !submitting && (!showMfa || mfaCode.length === 6);

  const redirectAfterLogin = (role: Parameters<typeof roleHomePath>[0]) => {
    navigate(roleHomePath(role), { replace: true });
    appToast.success('Signed in successfully.');
  };

  const signIn = async (nextEmail: string, nextPassword: string, nextMfa?: string) => {
    setSubmitting(true);
    try {
      const result = await login(nextEmail.trim(), nextPassword, nextMfa);
      if (result.mfaRequired) {
        setShowMfa(true);
        appToast.info('Enter your MFA code to continue.');
        return;
      }
      redirectAfterLogin(result.user.role);
    } catch (err) {
      toastApiError(err, 'Incorrect email or password.');
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    await signIn(email, password, showMfa ? mfaCode : undefined);
  };

  const quickLogin = async (demo: (typeof DEV_LOGIN_USERS)[number]) => {
    setEmail(demo.email);
    setPassword(demo.password);
    setShowMfa(false);
    setMfaCode('');
    await signIn(demo.email, demo.password);
  };

  return (
    <Fragment>
      <div className="credora-login">
        <div className="credora-login__shell">
          <aside className="credora-login__brand" aria-hidden="true">
            <div className="credora-login__brand-inner">
              <p className="credora-login__eyebrow">Royal Career &amp; Academic Tracking</p>
              <h1 className="credora-login__headline">
                One platform for
                <span> schools, parents &amp; counselors</span>
              </h1>
              <p className="credora-login__tagline">
                Track academics, attendance, and career pathways across every RCATS portal.
              </p>
              <ul className="credora-login__features">
                {FEATURES.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="credora-login__illustration">
                <img src={authIllustration} alt="" />
              </div>
            </div>
          </aside>

          <main className="credora-login__panel">
            <div className="credora-login__card">
              <Link className="credora-login__logo credora-brand-logo" to="/login">
                <img src={logo} alt="RCATS" className="img-fluid credora-brand-logo__img" />
              </Link>

              <div className="credora-login__form-header">
                <h4>Sign in to RCATS</h4>
                <p>Use your staff or parent credentials to access your portal.</p>
              </div>

              <Form className="theme-form credora-login__form" onSubmit={onSubmit} noValidate>
                <FormGroup>
                  <Label className="col-form-label" for="rcats-email">
                    Email Address
                  </Label>
                  <div className="credora-login__input-wrap">
                    <Mail className="credora-login__input-icon" size={18} aria-hidden="true" />
                    <Input
                      id="rcats-email"
                      className="form-control credora-login__input"
                      type="email"
                      required
                      autoComplete="username"
                      autoFocus
                      placeholder="you@school.org"
                      onChange={(e) => setEmail(e.target.value)}
                      value={email}
                    />
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label className="col-form-label" for="rcats-password">
                    Password
                  </Label>
                  <div className="credora-login__input-wrap">
                    <Lock className="credora-login__input-icon" size={18} aria-hidden="true" />
                    <Input
                      id="rcats-password"
                      className="form-control credora-login__input credora-login__input--password"
                      type={togglePassword ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      onChange={(e) => setPassword(e.target.value)}
                      value={password}
                    />
                    <button
                      type="button"
                      className="credora-login__toggle-password"
                      onClick={() => setTogglePassword(!togglePassword)}
                      aria-label={togglePassword ? 'Hide password' : 'Show password'}
                    >
                      {togglePassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </FormGroup>

                {showMfa ? (
                  <FormGroup className="credora-login__mfa">
                    <Label className="col-form-label" for="rcats-mfa">
                      MFA code
                    </Label>
                    <div className="credora-login__input-wrap">
                      <Shield className="credora-login__input-icon" size={18} aria-hidden="true" />
                      <Input
                        id="rcats-mfa"
                        className="form-control credora-login__input"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="6-digit code"
                        autoComplete="one-time-code"
                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                        value={mfaCode}
                      />
                    </div>
                  </FormGroup>
                ) : (
                  <button
                    type="button"
                    className="credora-login__mfa-toggle btn btn-link"
                    onClick={() => setShowMfa(true)}
                  >
                    Use MFA code
                  </button>
                )}

                <button
                  className="btn btn-primary credora-login__submit d-block w-100"
                  type="submit"
                  disabled={!canSubmit}
                >
                  {submitting ? (
                    <>
                      <span className="credora-login__spinner" aria-hidden="true" />
                      Signing in…
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>

                <p className="text-center mt-3 mb-0">
                  <Link to="/forgot-password">Forgot password?</Link>
                </p>
              </Form>

              {showDemoAccounts && (
                <div className="credora-login__dev-hint" role="note">
                  <strong>Seeded demo accounts</strong>
                  <p className="credora-login__dev-hint-text">
                    Password for all: <code>{DEMO_PASSWORD}</code>. Signs in against the local API.
                  </p>
                  <div className="credora-login__dev-buttons">
                    {DEV_LOGIN_USERS.map((u) => (
                      <button
                        key={u.email}
                        type="button"
                        className="credora-login__dev-btn"
                        disabled={submitting}
                        title={`${u.email} · ${DEMO_PASSWORD}`}
                        onClick={() => void quickLogin(u)}
                      >
                        {u.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <p className="credora-login__footer-note">
                Authorized school accounts only. Use a seeded demo account above for local testing.
              </p>
            </div>
          </main>
        </div>
      </div>
    </Fragment>
  );
}
